import { Box, Button, Input, KeyboardSpacer, Text } from '@betty/moonlight';
import { StackNavigationProp, useNavigation } from '@betty/navigation';
import {
  ElementRef,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from '@betty/i18n';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  ForgotPasswordErrors,
  ForgotPasswordNewPasswordProps,
} from './ForgotPasswordNewPassword.types';
import { resetPassword, selectResetPasswordDataState } from '../../state';

function ForgotPasswordNewPassword({
  token,
  accountid,
}: ForgotPasswordNewPasswordProps) {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const resetPasswordDataState = useSelector(selectResetPasswordDataState);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<ForgotPasswordErrors>({});

  const passwordRef = useRef<ElementRef<typeof Input>>(null);
  const confirmPasswordRef = useRef<ElementRef<typeof Input>>(null);

  const validatePassword = useCallback(
    (value: string) => {
      return value.length >= 8
        ? undefined
        : t('forgotPasswordMinimumCharactersPasswordError');
    },
    [t],
  );

  const clearErrors = useCallback((fieldName: keyof typeof errors) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
      submitError: undefined,
    }));
  }, []);

  const handlePasswordChange = useCallback(
    (value: string) => {
      setPassword(value);

      if (!value) {
        clearErrors('password');
      } else {
        const passwordError = validatePassword(value);

        let confirmPasswordError = undefined;
        if (confirmPassword.length > 0) {
          confirmPasswordError =
            value !== confirmPassword
              ? t('forgotPasswordDifferentPasswordsError')
              : undefined;
        }

        setErrors((prev) => ({
          ...prev,
          password: passwordError,
          ...(confirmPassword.length > 0 && {
            confirmPassword: confirmPasswordError,
          }),
          submitError: undefined,
        }));
      }
    },
    [validatePassword, confirmPassword, clearErrors, t],
  );

  const handleConfirmPasswordChange = useCallback(
    (value: string) => {
      setConfirmPassword(value);

      if (!value) {
        clearErrors('confirmPassword');
        return;
      }

      const lengthError = validatePassword(value);

      let matchError = undefined;
      if (password.length > 0) {
        matchError =
          value !== password
            ? t('forgotPasswordDifferentPasswordsError')
            : undefined;
      }

      setErrors((prev) => ({
        ...prev,
        confirmPassword: lengthError || matchError,
        submitError: undefined,
      }));
    },
    [validatePassword, password, t, clearErrors],
  );

  const onPasswordPress = useCallback(() => {
    confirmPasswordRef.current?.focus();
  }, []);

  const onSubmitPress = useCallback(async () => {
    try {
      await dispatch(
        resetPassword({
          token: decodeURIComponent(token),
          accountId: accountid,
          newPassword: password,
          newPasswordConfirmation: confirmPassword,
        }),
      ).unwrap();
      navigation.navigate('IntroScreen');
    } catch (_) {
      setErrors((prev) => ({
        ...prev,
        submitError: t('forgotPasswordGenericError'),
      }));
    }
  }, [token, accountid, password, confirmPassword, navigation, dispatch, t]);

  const isSubmitDisabled = useMemo(() => {
    return Boolean(
      errors.password ||
        errors.confirmPassword ||
        !password ||
        !confirmPassword,
    );
  }, [errors.password, errors.confirmPassword, password, confirmPassword]);

  return (
    <Box flex={1} justifyContent="space-between" marginTop="space-200">
      <Box gap="space-600">
        <Text
          variant="h4"
          gradient="darkSurfaceAccent"
          locNS="auth"
          locKey="forgotPasswordNewPasswordTitle"
        />
        <Text
          variant="body-m"
          color="darkSurfacePrimary"
          locNS="auth"
          locKey="forgotPasswordNewPasswordDescription"
        />
        <Input
          ref={passwordRef}
          placeholderKey="newPassword"
          placeholderNS="auth"
          keyboardType="visible-password"
          autoComplete="password"
          enterKeyHint="next"
          value={password}
          onChangeText={handlePasswordChange}
          onSubmitEditing={onPasswordPress}
          secureTextEntry
          error={errors.password}
        />
        {errors.password ? (
          <Text
            color="darkSurfaceError"
            variant="body-m"
            marginTop="-space-200"
          >
            {errors.password}
          </Text>
        ) : null}
        <Input
          ref={confirmPasswordRef}
          placeholderKey="confirmNewPassword"
          placeholderNS="auth"
          keyboardType="visible-password"
          autoComplete="password"
          enterKeyHint="go"
          value={confirmPassword}
          onChangeText={handleConfirmPasswordChange}
          onSubmitEditing={onSubmitPress}
          secureTextEntry
          error={errors.confirmPassword}
        />
        {errors.confirmPassword || errors.submitError ? (
          <Text
            color="darkSurfaceError"
            variant="body-m"
            marginTop="-space-200"
          >
            {errors.confirmPassword || errors.submitError}
          </Text>
        ) : null}
      </Box>
      <Box>
        <Button
          size="large"
          locNS="auth"
          locKey="forgotPasswordSubmitButton"
          onPress={onSubmitPress}
          loading={resetPasswordDataState === 'pending'}
          disabled={isSubmitDisabled}
        />
        <KeyboardSpacer isInsideSafeAreaView additionalOffset={-30} />
      </Box>
    </Box>
  );
}

export default memo(ForgotPasswordNewPassword);
